import React, { useEffect, useState } from 'react';
import { DsTable, DsLoader, DsButton, DsIcon } from '@tokenyico/design-system/dist/reactJS';
import { get, omitBy } from 'lodash';
import PropTypes from 'prop-types';

import './TableWrapper.less';
import { useTranslation } from 'react-i18next';

/**
 * TableWrapper component to ease DsTable usage
 * @component
 */
const TableWrapper = (props) => {
  const [filter, setFilter] = useState(props.filter ?? {});
  const [currentPage, setCurrentPage] = useState(props.currentPage ?? 1);
  const [rowsPerPage, setRowsPerPage] = useState(props.rowsPerPage);
  const [sortDirection, setSortDirection] = useState(props.sortDirection);
  const [sortBy, setSortBy] = useState(props.sortBy);
  const [columns, setColumns] = useState(props.header.map((h) => h.key));
  const [isLoading, setIsLoading] = useState(true);
  const [selectableRows, setSelectableRows] = useState([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [filtersBtnColor, setFiltersBtnColor] = useState('secondary');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const { t } = useTranslation();

  const keyColumn = props.keyColumn ?? 'id';
  let getKeyColumn = (row) => get(row, keyColumn);
  if (typeof keyColumn === 'function') getKeyColumn = keyColumn;

  const toggleFilters = () => {
    if (filtersBtnColor === 'secondary') {
      setFiltersBtnColor('primary');
      setIsFiltersOpen(true);
    } else {
      setFiltersBtnColor('secondary');
      setIsFiltersOpen(false);
    }
  };

  const clearFilters = async () => {
    setIsLoading(true);
    setCurrentPage(1);

    // we need to set the filters to actual values for them to be overridden
    const newFilter = {};
    for (const key of Object.keys(filter)) {
      if (Array.isArray(filter[key])) {
        newFilter[key] = []; // set array values to empty
      } else {
        newFilter[key] = null; // set other values to null
      }
    }
    setFilter(newFilter);
    if (props.onResetFilters) props.onResetFilters();
    if (props.onFetchData) {
      await props.onFetchData({ currentPage: 1, rowsPerPage, sortBy, sortDirection, filter: {} });
    }
    setIsLoading(false);
  };

  // if we're using isLoading, relay it
  useEffect(() => {
    if (typeof props.isLoading === 'boolean') setIsLoading(props.isLoading);
  }, [props.isLoading]);

  // on load
  useEffect(() => {
    loadData({
      filter,
      sortBy,
      sortDirection,
      currentPage,
      rowsPerPage,
    });
    // eslint-disable-next-line
  }, []);

  // on data change
  useEffect(() => {
    setSelectableRows([]);
    const newSelectableRows = [];
    for (const row of props.data) {
      newSelectableRows.push(getKeyColumn(row));
    }
    setSelectableRows(newSelectableRows);
    if (props.onSetSelectedRows) {
      props.onSetSelectedRows([]);
    }
    // eslint-disable-next-line
  }, [props.data]);

  useEffect(() => {
    setColumns(props.header.map((h) => h.key));
  }, [props.header]);

  // filter out empty arrays, empty strings, null, undefined values
  const omitValues = (value) =>
    (Array.isArray(value) && value.length === 0) || ['', null, undefined].includes(value);

  const loadData = async (fetchParams) => {
    if (typeof props.isLoading !== 'boolean') setIsLoading(true);

    const newFilter = omitBy(fetchParams.filter ?? {}, omitValues);

    setFilter(newFilter);
    setCurrentPage(fetchParams.currentPage);
    setRowsPerPage(fetchParams.rowsPerPage);
    setCurrentPage(fetchParams.currentPage);
    setSortBy(fetchParams.sortBy);
    setSortDirection(fetchParams.sortDirection);

    if (props.onFetchData) {
      await props.onFetchData({ ...fetchParams, filter: newFilter });
    }
    if (typeof props.isLoading !== 'boolean') setIsLoading(false);
    setIsFirstLoad(false);
  };

  // count of set filters
  const filterCount = Object.keys(omitBy(filter, omitValues)).length;

  return (
    <>
      {isFirstLoad && (
        <DsLoader version="dots" color="var(--primary)" className="_m:x(auto) _m:y(5) _p:y(5)" />
      )}
      <DsTable
        className={props.className}
        style={{ display: isFirstLoad ? 'none' : 'inherit' }}
        debounce={100}
        header={props.header}
        height={props.height}
        width={props.width}
        totalRows={props.totalRows}
        isSortable={props.isSortable ?? false}
        selectedRows={props.selectedRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        filter={filter}
        sortBy={sortBy}
        sortDirection={sortDirection}
        selectableRows={selectableRows}
        columns={columns}
        isLoading={isLoading}
        onSetColumns={({ detail }) => setColumns(detail)}
        onSetSelectedRows={({ detail }) => {
          if (props.onSetSelectedRows) {
            props.onSetSelectedRows(detail);
          }
        }}
        onLoadData={({ detail }) => loadData(detail)}
      >
        <div slot="actions">
          <div className={`row${props.children ? '' : ' no-filter'}`}>
            <div className="top-filters-section">
              {props.children && (
                <div className="filters-btn">
                  <DsButton
                    name="filters"
                    label={filterCount > 0 ? `Filters (${filterCount})` : 'Filters'}
                    type="button"
                    version={filtersBtnColor}
                    icon={isFiltersOpen ? 'up-open' : filterCount > 0 ? 'down-open' : 'filter'}
                    isMini={true}
                    isReverse={true}
                    onClick={toggleFilters}
                  />
                </div>
              )}
              {filterCount > 0 ? (
                <div className="filter-counter">
                  <div className="clear-filters" onClick={clearFilters}>
                    <DsIcon name="close" size=".75rem" color="var(--coolGrey-400)" />
                    <p>Clear filters</p>
                  </div>
                </div>
              ) : (
                ''
              )}
              {props.onExportCSV ? (
                <DsButton
                  label={t('shared.exportCSV')}
                  icon="download"
                  iconSize="1em"
                  onClick={props.onExportCSV}
                  isReverse
                  isMini
                />
              ) : null}
            </div>

            {props.actions && <div className="actions">{props.actions}</div>}
          </div>
        </div>

        <div
          slot="filters"
          className={isFiltersOpen ? 'filtersOpen filters' : 'filtersClose filters'}
        >
          {props.children}
        </div>

        <tbody>
          {Array.isArray(props.data) && props.data.length ? (
            props.data.map((row) => (
              <tr
                key={getKeyColumn(row)}
                className={props.selectedRows?.includes(getKeyColumn(row)) ? 'selected' : ''}
                onClick={() => {
                  if (props.onRowClick) {
                    props.onRowClick(row);
                  }
                }}
              >
                {columns.map((column) => (
                  <td key={`${getKeyColumn(row)}.${column}`}>
                    {props[column]
                      ? props[column]({ column, row, value: row[column] })
                      : row[column]}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length}>{props.noData ? props.noData() : ''}</td>
            </tr>
          )}
        </tbody>
      </DsTable>
    </>
  );
};

/*
export interface IFetchDataParams {
  filter?: Record<string, any>;
  sortBy?: string;
  sortDirection?: TableSortDirection;
  rowsPerPage?: number;
  currentPage?: number;
}
*/
TableWrapper.propTypes = {
  children: PropTypes.node, // slot="filter"
  header: PropTypes.arrayOf(
    PropTypes.exact({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      isSortable: PropTypes.bool,
      isSelectable: PropTypes.bool,
      info: PropTypes.string,
    }),
  ),
  keyColumn: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  data: PropTypes.arrayOf(PropTypes.any),
  totalRows: PropTypes.number,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.oneOf(['ASC', 'DESC']),
  filters: PropTypes.object,
  isClearFilters: PropTypes.bool,
  isLoading: PropTypes.bool,
  currentPage: PropTypes.number,
  rowsPerPage: PropTypes.number,
  isSortable: PropTypes.bool,
  selectedRows: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  noData: PropTypes.func,
  width: PropTypes.string,
  height: PropTypes.string,

  onFetchData: PropTypes.func,
  onSetSelectedRows: PropTypes.func,
  onRowClick: PropTypes.func,
  onResetFilters: PropTypes.func,
};
export default TableWrapper;
